/** Structured options editor (Slice 11) for a native enum-family field — replaces the flat textarea.
 * Per-option: a color swatch (6 semantic tokens + none), an editable value, reorder (rank), remove.
 * The intricate part is WHICH mutation each edit fires (plan D5 + the orphan-bug fix):
 *   - color / reorder / add  → batched into Save (set_field_config; new options have no cells yet).
 *   - rename an EXISTING value → fires eagerly on blur (rename_option rewrites stored cells).
 *   - remove an EXISTING value → fires eagerly (delete_option; blocked 409 while in use → kept + toast).
 * A newly-added (unsaved) row renames/removes purely locally until Save persists it. */
import { useState } from 'react';
import styles from './crm.module.css';
import type { CrmOptionGroup } from '../../hooks/useCrm';

export const OPTION_COLORS = ['green', 'blue', 'grey', 'orange', 'red', 'purple'] as const;

interface Row { value: string; color?: string; original: string | null } // original=null → newly added

interface Props {
  groups: CrmOptionGroup[] | null;
  flatOptions: string[];
  saving?: boolean;
  error?: string | null;
  onSetConfig: (groups: CrmOptionGroup[]) => void;
  // async so the editor can roll back a FAILED rename + prune only on a SUCCESSFUL delete — keeping
  // local rows in lockstep with server truth (a drifted row would let Save orphan/resurrect values).
  onRename: (from: string, to: string) => Promise<void>;
  onDelete: (value: string) => Promise<void>; // parent shows the 409-in-use toast; rejects → row kept
}

function initialRows(groups: CrmOptionGroup[] | null, flatOptions: string[]): Row[] {
  if (groups && groups.length) {
    return groups.flatMap((g) => (g.options ?? []).map((o) => ({ value: o.value, color: o.color, original: o.value })));
  }
  return flatOptions.map((v) => ({ value: v, color: undefined, original: v }));
}

export function CrmOptionsEditor({ groups, flatOptions, saving, error, onSetConfig, onRename, onDelete }: Props) {
  const [rows, setRows] = useState<Row[]>(() => initialRows(groups, flatOptions));

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const next = [...rs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const commitRename = (i: number) => {
    const r = rows[i];
    const trimmed = r.value.trim();
    if (r.original != null && !trimmed) {
      // a cleared EXISTING option is NOT a silent drop — revert it (removal must go through the × /
      // delete_option guarded path, else Save would drop an in-use value + orphan its cells).
      update(i, { value: r.original });
      return;
    }
    if (r.original != null && trimmed && trimmed !== r.original) {
      const from = r.original;
      update(i, { value: trimmed }); // optimistic display; `original` stays until the server confirms
      // eager rename (rewrites stored cells). On success mark saved under the new value; on FAILURE
      // (collision / network) roll the display back to the original so Save can't orphan cells.
      Promise.resolve(onRename(from, trimmed))
        .then(() => setRows((rs) => rs.map((x) => (x.original === from ? { ...x, original: trimmed } : x))))
        .catch(() => setRows((rs) => rs.map((x) => (x.original === from ? { ...x, value: from } : x))));
    } else if (trimmed !== r.value) {
      update(i, { value: trimmed });
    }
  };
  const remove = (i: number) => {
    const r = rows[i];
    if (r.original != null) {
      // eager delete (usage-guarded). Prune the row ONLY on success (by original identity, reorder-safe);
      // a 409-in-use rejects → keep the row so Save can't resurrect it and the parent toast explains.
      const val = r.original;
      Promise.resolve(onDelete(val))
        .then(() => setRows((rs) => rs.filter((x) => x.original !== val)))
        .catch(() => { /* kept; parent surfaces the in-use toast */ });
    } else {
      setRows((rs) => rs.filter((_, j) => j !== i)); // unsaved → drop locally
    }
  };
  const save = () => {
    // one implicit "Options" group (multi-group status default = Slice 12); drop blank/duplicate values.
    // A cleared EXISTING option reverts to its original here too (belt-and-braces vs. a missed blur) so
    // Save can NEVER drop a server option by omission — removals only happen via the guarded delete path.
    const seen = new Set<string>();
    const options: { value: string; color?: string }[] = [];
    for (const r of rows) {
      let v = r.value.trim();
      if (!v && r.original != null) v = r.original;
      if (!v || seen.has(v.toLowerCase())) continue;
      seen.add(v.toLowerCase());
      options.push(r.color ? { value: v, color: r.color } : { value: v });
    }
    onSetConfig([{ label: 'Options', options }]);
  };

  return (
    <div className={styles.optEditor}>
      {rows.map((r, i) => (
        <div key={i} className={styles.optRow}>
          <span className={styles.optReorder}>
            <button className={styles.optMove} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button className={styles.optMove} aria-label="Move down" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>↓</button>
          </span>
          <span className={styles.optSwatches}>
            <button
              className={`${styles.optSwatch} ${styles.optSwatchNone} ${!r.color ? styles.optSwatchOn : ''}`}
              aria-label="No color" title="No color" onClick={() => update(i, { color: undefined })}
            >∅</button>
            {OPTION_COLORS.map((c) => (
              <button
                key={c}
                className={`${styles.optSwatch} ${styles[`pill${c[0].toUpperCase()}${c.slice(1)}` as keyof typeof styles]} ${r.color === c ? styles.optSwatchOn : ''}`}
                aria-label={c} title={c} onClick={() => update(i, { color: c })}
              />
            ))}
          </span>
          <input
            className={styles.optValue}
            value={r.value}
            placeholder="option…"
            onChange={(e) => update(i, { value: e.target.value })}
            onBlur={() => commitRename(i)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          />
          <button className={styles.optRemove} aria-label={`Remove ${r.value || 'option'}`} onClick={() => remove(i)}>×</button>
        </div>
      ))}
      {error && <div className={styles.modalError} role="alert">{error}</div>}
      <div className={styles.optActions}>
        <button className={styles.filterAdd} onClick={() => setRows((rs) => [...rs, { value: '', original: null }])}>+ Add option</button>
        <button className={styles.gridPageBtn} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save options'}</button>
      </div>
    </div>
  );
}
